import React, { useRef, useState } from "react";
/** Assets */
import defaultBackGroundImage from "assets/images/defaultAppBackgroundImage.png";
import { Bars3Icon, PlusIcon } from "@heroicons/react/24/outline";
import HomeIcon from "assets/icons/HeroIcons/HomeIcon";
import { BellIcon } from "assets/icons/HeroIcons";
import {
  ArrowUpRightIcon,
  BinocularsIcon,
  CameraIcon,
  CheckCircleIcon,
  FolderListIcon,
  User2Icon,
} from "assets/icons/HeroIcons";
import ISNLogo from "assets/logo/ISNLogo";
/** Components */
import { Toasts } from "view/components/Toasts";
/** Services */
import apiLibrary from "services/api";
import usePermissions from "hooks/usePermissions";
import { getCurrentBranding } from "utils/customBranding";
import { brandLogos } from "hooks/useCustomBranding/constants";
import { MAXIMUM_FILE_SIZE } from "constants/FileSize";
import { setLoadingProgress } from "store/loadingBar";
import { getRandomProgress } from "utils/getRandomProgress";
import { useDispatch } from "react-redux";
import useCustomBranding from "hooks/useCustomBranding";

interface ImageData {
  title: string;
  value: any | null;
  description: string;
  imageKey: string;
  fileId: number | null;
  filePath: string | null;
  appName: string;
  getUniversalSettings: () => void;
  setSettings: any;
}
export const ObserverMobileDashboard: React.FC<ImageData> = ({
  title,
  value,
  description,
  imageKey,
  fileId,
  filePath,
  appName,
  getUniversalSettings,
  setSettings,
}) => {
  const dispatch = useDispatch();
  const { universalSettings, universalSettingsImages } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);

  const currentBrand = getCurrentBranding();
  const CURRENT_BRAND_LOGO = brandLogos[currentBrand];

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { primaryColor } = useCustomBranding();

  /**
   * Handles the upload of a user's profile image.
   * Accepts only JPEG/JPG/PNG with EXACT dimensions of 1080×1920.
   */
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = event.target;
    const file = input.files?.[0];

    const resetInput = () => {
      input.value = "";
    };

    const getImageDimensions = (
      f: File
    ): Promise<{ width: number; height: number }> =>
      new Promise((resolve, reject) => {
        const url = URL.createObjectURL(f);
        const img = new Image();
        img.onload = () => {
          const dims = { width: img.width, height: img.height };
          URL.revokeObjectURL(url);
          resolve(dims);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Invalid image file."));
        };
        img.src = url;
      });

    try {
      if (!file) return;

      // Only allow raster JPEG/JPG/PNG
      const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validImageTypes.includes(file.type)) {
        Toasts.error("Please select a valid image file (jpeg/jpg/png).");
        resetInput();
        return;
      }

      // Max size 25 MB
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 25) {
        Toasts.error("File size must be less than 25 MB!");
        resetInput();
        return;
      }

      // STRICT dimension check (must be exactly 1080×1920)
      const { width, height } = await getImageDimensions(file);
      if (width !== 1080 || height !== 1920) {
        Toasts.error("Please ensure the image is exactly 1080×1920 pixels.");
        resetInput();
        return;
      }
      dispatch(setLoadingProgress(getRandomProgress()));

      setIsLoading(true);

      const uploadResponse = await apiLibrary.file.fileUpload(file);

      const { message } = await apiLibrary.systemSettings.updateUniversalImages(
        imageKey,
        appName,
        uploadResponse.data.id
      );

      Toasts.success(message);
      await getUniversalSettings();
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ??
        error?.message ??
        "Something went wrong.";
      Toasts.error(errorMsg);
      resetInput();
    } finally {
      setIsLoading(false);
      dispatch(setLoadingProgress(100));
    }
  };

  /**
   * Handles the click event to trigger the file input element.
   */
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetToDefault = async () => {
    if (fileId === null) return;
    try {
      setIsLoading(true);
      dispatch(setLoadingProgress(getRandomProgress()));

      const { data, message } =
        await apiLibrary.systemSettings.updateUniversalImages(
          imageKey,
          appName,
          null
        );
      Toasts.success(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      getUniversalSettings();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
      dispatch(setLoadingProgress(100));
    }
  };

  return (
    <div className="w-full h-[342px] col-span-1 ">
      <div>
        <div>
          <p className="font-semibold text-secondaryMid dark:text-textMain text-base">
            Observer Mobile Dashboard
          </p>
          <p className="text-sm text-textMidLight dark:text-textMain">
            For the best results on all devices,
            <br /> use an image that's 1080 x 1920 px.
          </p>
          <button
            type="button"
            onClick={resetToDefault}
            disabled={isLoading || fileId === null}
            className="text-primary text-xs font-semibold mt-2 cursor-pointer disabled:cursor-not-allowed"
          >
            Reset to default
          </button>
        </div>

        <div className="relative w-[140px] bg-bgBluish dark:bg-bgMain rounded-md">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center h-[160px] rounded-md z-10"
            style={{
              backgroundImage: `url(${filePath ? filePath : defaultBackGroundImage})`,
            }}
          />
          {/* background overlay */}
          <div className="absolute inset-0 bg-[#a19e9e] h-[160px] bg-opacity-50 rounded-md z-10"></div>
          {/* Status Bar */}
          <div className="px-[6px] pt-[1px] pb-1 flex justify-between items-center text-white text-sm relative z-10">
            <span className="text-[7.52px] text-textWhite font-medium">
              9:30
            </span>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              disabled={isLoading}
            />

            <button
              onClick={handleClick}
              disabled={isLoading}
              className="h-8 w-8 disabled:cursor-not-allowed flex justify-center items-center rounded-full bg-bgWhite dark:bg-secondaryLight absolute -right-[15px] -top-[15px]"
            >
              <CameraIcon className="w-5 h-5" />
            </button>
          </div>
          {/* progress bar */}
          <div className="flex items-center w-full relative px-[5px] z-10">
            <div className="w-full h-[10px] bg-primary rounded-full flex items-center justify-start shadow-[inset_-12px_-8px_40px_#46464620] ps-[1.5px]">
              <div className="w-[40%] h-[6px]  bg-bgWhite rounded-md"></div>
            </div>
          </div>

          {/* Header actions buttons */}
          <div className="bg-blue-700 px-[6px] pt-[10px] pb-2 relative z-20">
            <div className="flex items-center justify-between mb-2">
              <div className="">
                <Bars3Icon className="w-3 h-3 text-textWhite z-50 relative cursor-pointer" />
              </div>
              <div className="bg-primaryExtraLight p-1 rounded-sm cursor-pointer z-50 relative">
                <ISNLogo width={8} height={8} />
              </div>
              <div className="bg-primaryExtraLight p-[2px] rounded-full z-50 relative cursor-pointer flex items-center justify-center">
                <User2Icon className="w-2 h-2" fill="#005C89" />
              </div>
            </div>

            <h1 className="text-textWhite text-[9.52px] font-semibold">
              Welcome, Amanda
            </h1>
          </div>
          {/* Main Content Area */}
          <div className="px-1 space-y-4 relative z-20">
            {/* Dashboard Cards */}
            <div className="flex justify-between">
              {/* Large Card */}
              <div
                className="bg-[#ebf2f64a] rounded-[4px] p-6 h-[54.82px] w-[74px] relative"
                style={{ boxShadow: "0 1px 1px #00000040" }}
              >
                <div className="absolute bottom-[6px] right-1 w-[10px] h-[10px] bg-[#ffffff47] dark:stroke-textWhite rounded-full flex items-center justify-center">
                  <ArrowUpRightIcon
                    className="w-[6px] h-[6px]"
                    fill="#FFFFFF"
                  />
                </div>
              </div>

              {/* Small Cards Column */}
              <div className="flex flex-col justify-between gap-1">
                <div
                  className="bg-[#ebf2f64a] rounded-[4px] p-3 h-[26px] w-[54px] relative"
                  style={{ boxShadow: "0 1px 1px #00000040" }}
                >
                  <div className="absolute bottom-1 right-1 w-[9px] h-[9px] bg-[#ffffff47] rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon
                      className="w-[5px] h-[5px] dark:stroke-textWhite dark:fill-textWhite"
                      fill="#FFFFFF"
                    />
                  </div>
                </div>
                <div
                  className="bg-[#ebf2f64a] rounded-[4px] p-3 h-[26px] w-[54px] relative"
                  style={{ boxShadow: "0 1px 1px #00000040" }}
                >
                  <div className="absolute bottom-1 right-1 w-[9px] h-[9px] bg-[#ffffff47] rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon
                      className="w-[5px] h-[5px] dark:stroke-textWhite"
                      fill="#FFFFFF"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Measurement Display with dashed border */}
          <div className="relative mt-[22px] px-1 h-[80px]">
            <div className="flex justify-between absolute top-[-15px] gap-1 z-20">
              <div className="bg-bgWhite dark:bg-bgtetriary h-[47px] w-[63px] rounded-[4px] relative shadow-[0_1px_1px_#bcbcbc7d] dark:shadow-[0_1px_1px_#00000075]">
                <div className="absolute top-1 right-1">
                  <CheckCircleIcon className="w-3 h-3" fill="#009F88" />
                </div>
                <div className="absolute bottom-1 right-1 w-3 h-3 bg-primaryLight dark:bg-secondaryMidLight rounded-full flex items-center justify-center">
                  <ArrowUpRightIcon className="w-2 h-2" fill="#005C89" />
                </div>
              </div>
              <div className="bg-bgWhite dark:bg-bgtetriary h-[47px] w-[63px] rounded-[4px] relative shadow-[0_1px_1px_#bcbcbc7d] dark:shadow-[0_1px_1px_#00000075]">
                <div className="absolute top-1 right-1">
                  <CheckCircleIcon className="w-3 h-3" fill="#009F88" />
                </div>
                <div className="absolute bottom-1 right-1 w-3 h-3 bg-primaryLight dark:bg-secondaryMidLight rounded-full flex items-center justify-center">
                  <ArrowUpRightIcon className="w-2 h-2" fill="#005C89" />
                </div>
              </div>
            </div>

            {/* Add Observation Button */}
            <div className="px-1 relative z-10 top-[43px]">
              <button className="w-full bg-primary py-1 gap-1 text-[9.15px] font-bold rounded-xl flex items-center justify-center text-textWhite">
                <PlusIcon className="w-2.5 h-2.5" />
                <span>Add Observation</span>
              </button>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="bg-bgWhite dark:bg-secondaryLight border-t border-borderLightGray dark:border-bgMain relative z-10 shadow-sm rounded-b-md overflow-hidden">
            <div className="flex justify-between items-center">
              <div className="flex flex-col items-center border-b border-b-primary h-full w-full py-[3px]">
                <HomeIcon
                  className="w-3 h-3 fill-primary !dark:fill-primary"
                  fill={primaryColor}
                />
                <span className="text-[4px] font-medium text-primary">
                  Home
                </span>
              </div>
              <div className="flex flex-col items-center  w-full py-[3px]">
                <BinocularsIcon className="w-3 h-3" />
                <span className="text-[4px] font-medium text-secondaryMid dark:text-textWhite">
                  Observations
                </span>
              </div>
              <div className="flex flex-col items-center  w-full py-[3px]">
                <FolderListIcon className="h-3 w-3" height={12} width={12} />
                <span className="text-[4px] font-medium text-secondaryMid dark:text-textWhite">
                  Programs
                </span>
              </div>
              <div className="flex flex-col items-center  w-full py-[3px]">
                <BellIcon className="w-3 h-3 relative" />
                <div className="absolute top-[5px] -mr-[5px] h-[5px] w-[5px] rounded-full bg-bgWhite flex items-center justify-center">
                  <div className="h-[3px] w-[3px] rounded-full bg-primary"></div>
                </div>
                <span className="text-[4px] font-medium text-secondaryMid dark:text-textWhite">
                  Notifications
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
